import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CartItemInput, CartLine, CartRecord, CommerceProduct } from '../../models/commerce.models';

export interface SummaryDraftLine { key: number | string; name: string; amount: number; included?: string[]; }

@Component({ selector: 'app-order-summary', templateUrl: './order-summary.component.html',
    styleUrls: ['./order-summary.component.scss'] })
export class OrderSummaryComponent {
    @Input() cart: CartRecord;
    @Input() products: CommerceProduct[] = [];
    @Input() editingItem: CartItemInput;
    @Input() editingItemId: string;
    @Input() drafts: SummaryDraftLine[] = [];
    @Input() disabled = false;
    @Input() editable = true;
    @Input() allowEdit = false;
    @Input() estimated = false;
    @Input() shippingMessage = '';
    @Input() shippingBusy = false;
    get hasShipping(): boolean { return !!this.cart?.cart.lines.some(line => line.requiresShipping && line.input.deliveryMethod === 'mail'); }
    @Output() removeLine = new EventEmitter<CartLine>();
    @Output() editLine = new EventEmitter<CartLine>();
    @Output() removeDraft = new EventEmitter<number | string>();
    @Output() applyCode = new EventEmitter<string>();
    @Output() codeEditing = new EventEmitter<boolean>();
    codeOpen = false;
    code = '';
    get quote() { return this.cart?.cart.quote; }
    get currency(): string { return this.quote?.currency || 'USD'; }
    get lines(): CartLine[] { return this.cart?.cart.lines.filter(l => !l.parentItemId) || []; }
    get savedCode(): string { return this.cart?.cart.promotionCode || ''; }
    get validCode(): boolean { return !!this.savedCode && !!this.quote && !this.quote.issues.some(i => i.startsWith('promotion_')); }
    get draftTotal(): number { return this.drafts.reduce((sum, line) => sum + line.amount, 0); }
    get subtotal(): number { return (this.quote?.subtotal ?? this.lines.reduce((sum, l) => sum + this.original(l), 0)) + this.draftTotal; }
    get total(): number { return this.quote?.total == null ? null : this.quote.total + this.draftTotal; }
    get orderTotal(): number { return this.hasShipping && this.quote?.shipping == null ? null : this.subtotal + (this.quote?.shipping || 0); }
    name(line: CartLine): string {
        const p = line.input.participant;
        return p ? [p.firstName, p.lastName].join(' ') : line.input.kind === 'donation' ? 'Streaker Family Donation' : line.description;
    }
    children(line: CartLine): CartLine[] { return this.cart.cart.lines.filter(l => l.parentItemId === line.itemId); }
    description(line: CartLine): string {
        const selection = line.parentItemId === this.editingItemId && this.editingItem?.swag?.productId === line.input.productId
            ? this.editingItem.swag : line.input;
        const size = this.products.find(product => product.productId === +selection.productId)?.sizeLabels?.[selection.productSizeId];
        return line.description + (size ? ' - ' + size : '');
    }
    original(line: CartLine): number { return [line, ...this.children(line)].reduce((sum, l) => sum + l.unitPrice * l.input.quantity, 0); }
    discount(line: CartLine): number { return [line, ...this.children(line)].reduce((sum, l) => sum + (l.discount || 0), 0); }
    openCode(): void { this.code = this.savedCode; this.codeOpen = true; this.codeEditing.emit(true); }
    cancelCode(): void { this.codeOpen = false; this.codeEditing.emit(false); }
    submitCode(): void { if (this.disabled || !this.code.trim()) { return; } this.cancelCode(); this.applyCode.emit(this.code.trim()); }
    clearCode(): void { if (!this.disabled) { this.cancelCode(); this.applyCode.emit(''); } }
}
